import { filterSeparatorRects } from "@libertify.frontend/helpers";
import {
  CustomSpinner,
  DEFAULT_HIGHLIGHT_ROUGHNESS,
  HighlightPopup,
  RoughNotationConfig,
  RoughNotationHighlight,
  radius,
} from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { Tooltip } from "antd";
import { filterTextHighlightRects } from "helpers/highlights";
import { useAntTheme } from "hooks/useAntTheme";
import { produce } from "immer";
import { useCuepointsStore } from "pages/media/components/animator/useCuepointsStore";
import {
  PdfHighlight,
  useHighlightsStore,
} from "pages/media/components/animator/useHighlightsStore";
import HighlightZoom from "pages/media/components/binder/HighlightZoom";
import { MediaDocument } from "pages/media/types";
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  AreaHighlight,
  Highlight,
  IHighlight,
  NewHighlight,
  PdfHighlighter,
  PdfLoader,
  Popup,
} from "react-pdf-highlighter";
import "react-pdf-highlighter/dist/style.css";
import { useVolatileBoundStore } from "store";
import styled from "styled-components";
import { lightenColor } from "utils";
import { PDFJS_WORKER_SRC } from "utils/pdfjsConfig";
import { Uuid } from "./UUID";

export const PdfHighlightsViewer: React.FC<{
  document: MediaDocument;
  addHighlight?: (h: NewHighlight) => void;
  startScrollTo: string;
  onHighlightClick?: (highlight: PdfHighlight) => void;
  pdfScaleValue?: string;
}> = ({ document, addHighlight, startScrollTo, onHighlightClick }) => {
  const t = useTranslate();
  const scrollViewerTo = useRef((highlight: PdfHighlight) => {});

  const { theme } = useAntTheme();

  const { cuepoints, setCurrentCuepoint, updateCount } = useCuepointsStore();
  const {
    currentHighlight,
    highlights,
    setCurrentHighlight,
    isEditing,
    sectionHighlights,
  } = useHighlightsStore();

  const { highlightRanks } = useVolatileBoundStore((state) => ({
    highlightRanks: state.storyboardState.highlightRanks,
  }));

  const hasCurrentHighlight = Boolean(currentHighlight);

  //To filter highlight on pdf by rank
  const highlightsToDisplay = useMemo(() => {
    if (!highlightRanks) {
      return highlights;
    }

    const highlightIdByRanks = new Set(
      sectionHighlights
        .filter(
          (sectionHighlight) =>
            highlightRanks.includes(sectionHighlight.rank) ||
            (highlightRanks.includes(5) && sectionHighlight.rank > 5)
        )
        .map((sectionHighlight) => sectionHighlight.sourceId)
    );

    return highlights.filter((highlight) =>
      highlightIdByRanks.has(highlight.id)
    );
  }, [highlightRanks, highlights, sectionHighlights]);

  const getHighlightById = useCallback(
    (id: string) => {
      return highlightsToDisplay?.find((highlight) => highlight.id === id);
    },
    [highlightsToDisplay]
  );

  useEffect(() => {
    const scrolledHighlight = getHighlightById(startScrollTo);

    // To not scroll everytime when highlight is called by InspectorToolbar
    // Only handle PdfHighlight (has position property)
    if (scrolledHighlight && "position" in scrolledHighlight && !isEditing) {
      // Due to differences in position generation between backend and PDFViewer, we need to adjust the scroll top by adding 100px for generated highlights (HLs) and subtracting 100px for user-created highlights.
      const adjustment = scrolledHighlight.position?.usePdfCoordinates
        ? 100
        : -100;

      const scrolledHighlightAdjustedPosition = produce(
        scrolledHighlight,
        (draft) => {
          if (draft.position.boundingRect) {
            draft.position.boundingRect.y1 += adjustment;
            draft.position.boundingRect.y2 += adjustment;
          }
        }
      );

      scrollViewerTo.current(scrolledHighlightAdjustedPosition);
    }
  }, [
    startScrollTo,
    currentHighlight,
    updateCount,
    isEditing,
    getHighlightById,
  ]);

  const resetHash = () => {
    globalThis.document.location.hash = "";
  };

  const handleAddHighlight = (newhighlight: NewHighlight) => {
    const isImage = newhighlight.position.rects.length === 0;
    const highlight = isImage
      ? newhighlight
      : filterTextHighlightRects(newhighlight);

    // backend only support integer values
    highlight.position.boundingRect.height = Math.round(
      highlight.position.boundingRect.height
    );
    highlight.position.boundingRect.width = Math.round(
      highlight.position.boundingRect.width
    );
    highlight.position.rects.forEach((rect) => {
      rect.height = Math.round(rect.height);
      rect.width = Math.round(rect.width);
    });

    // Reload the inspector if user didn't close it before creating new highlight
    setCurrentHighlight(undefined);
    setCurrentCuepoint(undefined);

    addHighlight(highlight);
  };

  if (!document.url) {
    return null;
  }

  return (
    <StyledPdfLoaderWrapper>
      <PdfLoader
        url={document.url}
        beforeLoad={<CustomSpinner />}
        onError={console.error}
        workerSrc={PDFJS_WORKER_SRC}
      >
        {(pdfDocument) => (
          <PdfHighlighter
            pdfDocument={pdfDocument}
            enableAreaSelection={(event) => event.altKey}
            onScrollChange={resetHash}
            scrollRef={(scrollTo) => {
              scrollViewerTo.current = scrollTo;
            }}
            onSelectionFinished={(position, content, hideTipAndSelection) => (
              <div
                role="button"
                tabIndex={0}
                className="Tip__compact"
                style={{
                  backgroundColor: theme.colorPrimary,
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleAddHighlight({
                    content,
                    position,
                    comment: { text: "", emoji: "" },
                  });
                  hideTipAndSelection();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleAddHighlight({
                      content,
                      position,
                      comment: { text: "", emoji: "" },
                    });
                    hideTipAndSelection();
                  }
                }}
              >
                {t("components.binder.AnimationWeb.addHighlight")}
              </div>
            )}
            highlightTransform={(
              highlight,
              index,
              setTip,
              hideTip,
              _viewportToScaled,
              _screenshot,
              isScrolledTo
            ) => {
              const isTextHighlight = !highlight?.content?.image;
              const isCurrentHighlight = currentHighlight?.id === highlight.id;

              const idStylesRelative: CSSProperties = {
                position: "relative",
                zIndex: 1000,
                left: -10,
                top: 0,
                color: "#fff",
                background:
                  currentHighlight?.id === highlight.id
                    ? theme.colorPrimary
                    : theme.colorBgSpotlight,
                fontSize: 11,
                padding: 3,
                borderRadius: radius.pill,
                minWidth: 20,
                textAlign: "center",
                width: "fit-content",
                fontWeight: "bold",
                opacity: 0.8, // to not cover pdf content
              };

              const idStylesAbsolute: CSSProperties = {
                position: "absolute",
                zIndex: 1000,
                left: highlight?.position.boundingRect.left,
                top: highlight?.position.boundingRect.top,
                color: "#fff",
                background: isCurrentHighlight
                  ? theme.colorPrimary
                  : theme.colorBgSpotlight,
                fontSize: 11,
                padding: 3,
                borderRadius: radius.pill,
                fontWeight: "bold",
                opacity: 0.8,
              };

              const currentCuepoint = cuepoints?.find((cuepoint) => {
                if ("sourceId" in cuepoint) {
                  return cuepoint.sourceId === highlight.id;
                }
                return cuepoint.highlightId === highlight.id;
              });

              const currentRoughNotation = currentCuepoint?.roughNotation;
              let component;

              let highlightIndex;

              if (highlightsToDisplay) {
                highlightIndex = highlightsToDisplay?.findIndex(
                  (h) => h.id === highlight.id
                );
              }

              if (
                currentCuepoint?.roughNotation === undefined ||
                Boolean(currentCuepoint?.zoom)
              ) {
                component = isTextHighlight ? (
                  <div>
                    <Highlight
                      isScrolledTo={isScrolledTo}
                      position={highlight.position}
                      comment={highlight.comment}
                      onClick={() =>
                        onHighlightClick(highlight as PdfHighlight)
                      }
                    />
                    <Tooltip
                      autoAdjustOverflow
                      title={
                        <Uuid id={highlight?.id} style={{ color: "#fff" }} />
                      }
                    >
                      <div style={idStylesAbsolute}>{highlightIndex + 1}</div>
                    </Tooltip>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onMouseDown={() => onHighlightClick(highlight)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onHighlightClick(highlight);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Tooltip
                      autoAdjustOverflow
                      title={
                        <Uuid id={highlight?.id} style={{ color: "#fff" }} />
                      }
                    >
                      <div style={idStylesAbsolute}>{highlightIndex + 1}</div>
                    </Tooltip>
                    <AreaHighlight
                      isScrolledTo={isScrolledTo}
                      highlight={highlight}
                      onChange={(boundingRect) => {
                        // change not supported yet,
                        // this code also make the highlight moves to another position
                        // when using pdfCoordinates = true
                        // MED-1014
                        // handleUpdateHighlight(
                        //   highlight.id,
                        //   {
                        //     boundingRect: viewportToScaled(boundingRect),
                        //   },
                        //   { image: screenshot(boundingRect) }
                        // );
                      }}
                    />
                    {currentCuepoint?.zoom && highlight.content.image && (
                      <HighlightZoom
                        src={highlight.content.image}
                        position={highlight.position}
                      />
                    )}
                  </div>
                );
              } else {
                const hasImage = highlight.position.rects.length === 0;
                const shouldRenderRects = !(
                  !["highlight", "underline", "strike-through"].includes(
                    currentRoughNotation.type
                  ) || hasImage
                );

                const rects = shouldRenderRects
                  ? filterSeparatorRects(highlight.position.rects)
                  : [highlight.position.boundingRect];

                // make the active highlight more visible
                const color =
                  isCurrentHighlight || !hasCurrentHighlight
                    ? currentRoughNotation?.color
                    : lightenColor(currentRoughNotation?.color, 0.7);

                component = (
                  <RoughNotationHighlight
                    rects={rects}
                    config={
                      {
                        type: currentRoughNotation.type,
                        color,
                        animate: currentRoughNotation?.animate,
                        animationDelay: currentRoughNotation?.animationDelay,
                        animationDuration:
                          currentRoughNotation?.animationDuration,
                        strokeWidth: currentRoughNotation?.strokeWidth,
                        padding: currentRoughNotation?.padding,
                        brackets: currentRoughNotation?.brackets,
                        iterations: currentRoughNotation?.iterations,
                        multiline: currentRoughNotation?.multiline,
                      } as RoughNotationConfig
                    }
                    roughness={DEFAULT_HIGHLIGHT_ROUGHNESS}
                    keyPrefix={`rect-${highlight.id}`}
                    renderExtra={(rect, rectIndex) =>
                      rectIndex === 0 ? (
                        <Tooltip
                          title={
                            <Uuid
                              id={highlight?.id}
                              style={{ color: "#fff" }}
                              tooltip={false}
                            />
                          }
                        >
                          <div
                            style={{
                              ...idStylesRelative,
                              position: "absolute",
                              zIndex: 1001,
                              left: rect.left - 20,
                              top: rect.top - 5,
                            }}
                          >
                            {highlightIndex + 1}
                          </div>
                        </Tooltip>
                      ) : null
                    }
                    renderContent={(rect) => (
                      <button
                        type="button"
                        onClick={() => onHighlightClick(highlight)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            onHighlightClick(highlight);
                          }
                        }}
                        style={{
                          height: rect.height,
                          width: rect.width,
                          cursor: "pointer",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                        }}
                        aria-label="View highlight"
                      />
                    )}
                  />
                );
              }

              return (
                <Popup
                  popupContent={<HighlightPopup {...highlight} />}
                  onMouseOver={(popupContent) =>
                    setTip(highlight, (highlight) => popupContent)
                  }
                  onMouseOut={hideTip}
                  key={index}
                >
                  {component}
                </Popup>
              );
            }}
            highlights={highlightsToDisplay as IHighlight[]}
          />
        )}
      </PdfLoader>
    </StyledPdfLoaderWrapper>
  );
};

const StyledPdfLoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  /* todo : fix blend mode on .textLayer and .PdfHighlighter__highlight-layer
   https://stackoverflow.com/questions/31629541/remove-mix-blend-mode-from-child-element */
  .textLayer {
    /*
     * keep blend mode to show highlighter opacity on creation
     * find a better solution for zoom effect in the future
     * mix-blend-mode: normal;
     */
  }
  .AreaHighlight__part {
    background: rgba(255, 226, 143, 0.4);
  }
  .PdfHighlighter__highlight-layer .rough-annotation {
    opacity: 0.35;
  }
`;
